import React, { memo } from 'react';

import { shallowEqualObjects } from 'shallow-equal';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';
import { usePostOfficePlacement } from '@atlassian/conversation-assistant-utils/usePostOfficePlacement';

import { useRovoChatInputPlacementProps } from './use-rovo-chat-input-placement-props';

const styles = cssMap({
	sidePanel: {
		marginLeft: token('space.200'),
		marginRight: token('space.200'),
	},
	fullScreen: {
		width: 'min(800px, 95vw)',
		marginInline: 'auto',
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
});

const RemoteChatInputPlacementComponent = ({
	placementId,
	fullScreen,
}: {
	placementId: string;
	fullScreen?: boolean;
}): React.JSX.Element | null => {
	const placementProps = useRovoChatInputPlacementProps();
	const { PostOfficePlacement } = usePostOfficePlacement();

	if (!PostOfficePlacement) {
		return null;
	}

	return (
		<UFOSegment name="chat-input-placement">
			<Box xcss={fullScreen ? styles.fullScreen : styles.sidePanel}>
				<PostOfficePlacement placementId={placementId} placementProps={placementProps} />
			</Box>
		</UFOSegment>
	);
};

// Experiment-only: absorb parent render waves when remote placement props are stable.
const MemoizedRemoteChatInputPlacement = memo(
	RemoteChatInputPlacementComponent,
	shallowEqualObjects,
);

type RemoteChatInputPlacementProps = React.ComponentProps<typeof RemoteChatInputPlacementComponent>;

export const RemoteChatInputPlacement = (
	props: RemoteChatInputPlacementProps,
): React.JSX.Element | null => {
	return FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	) ? (
		<MemoizedRemoteChatInputPlacement {...props} />
	) : (
		<RemoteChatInputPlacementComponent {...props} />
	);
};
